import { createFileRoute } from '@tanstack/react-router';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconId, IconUsers } from '@tabler/icons-react';
import { useState } from 'react';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { QuickCreateButton, type FieldDef } from '@/shared/components/QuickCreateButton';
import { EmployeeRolesDrawer, type RolesEmployee } from '@/features/workforce/EmployeeRolesDrawer';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Employee extends Denormalized {
  type: 'erp-employees';
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  employeeCode?: string;
  contractType?: string;
  employeeType?: string;
  isActive?: boolean;
}

const FIELDS: FieldDef[] = [
  { name: 'firstName', label: 'Nome', required: true },
  { name: 'lastName', label: 'Cognome', required: true },
  { name: 'email', label: 'Email aziendale' },
  { name: 'personalEmail', label: 'Email personale' },
  { name: 'phone', label: 'Telefono' },
  { name: 'employeeCode', label: 'Codice dipendente' },
  { name: 'taxCode', label: 'Codice fiscale' },
  { name: 'birthDate', label: 'Data di nascita', kind: 'date' },
  { name: 'hiredDate', label: 'Data assunzione', kind: 'date' },
  { name: 'terminatedDate', label: 'Data cessazione', kind: 'date' },
  {
    name: 'contractType', label: 'Tipo contratto', kind: 'select', defaultValue: 'full-time',
    options: [
      { value: 'full-time', label: 'Full-time' },
      { value: 'part-time', label: 'Part-time' },
      { value: 'consultant', label: 'Consulente' },
      { value: 'intern', label: 'Stage' },
    ],
  },
  {
    name: 'employeeType', label: 'Tipo dipendente', kind: 'select', defaultValue: 'internal',
    options: [
      { value: 'internal', label: 'Interno' },
      { value: 'external', label: 'Esterno' },
    ],
  },
  { name: 'site', label: 'Sede di lavoro', kind: 'reference', refType: 'erp-sites', refLabelFields: ['name'] },
  { name: 'businessGroup', label: 'Gruppo', kind: 'reference', refType: 'erp-business-groups', refLabelFields: ['name'] },
  { name: 'defaultProject', label: 'Commessa di default', kind: 'reference', refType: 'project-entities', refLabelFields: ['name'] },
  { name: 'isProductive', label: 'Produttivo', kind: 'boolean', defaultValue: false },
  { name: 'isEfficient', label: 'Efficiente', kind: 'boolean', defaultValue: false },
  { name: 'isActive', label: 'Attivo', kind: 'boolean', defaultValue: true },
];

export const Route = createFileRoute('/_app/workforce/')({ component: EmployeesPage });

function EmployeesPage() {
  const [rolesFor, setRolesFor] = useState<RolesEmployee | null>(null);
  return (
    <>
      <ResourceListPage<Employee>
        title=""
        type="erp-employees"
        columns={[
          { accessorKey: 'employeeCode', header: 'Codice', size: 110 },
          { accessorKey: 'firstName', header: 'Nome' },
          { accessorKey: 'lastName', header: 'Cognome' },
          { accessorKey: 'email', header: 'Email' },
          { accessorKey: 'phone', header: 'Telefono' },
          { accessorKey: 'contractType', header: 'Contratto', size: 120 },
          { accessorKey: 'isActive', header: 'Attivo', size: 90, Cell: ({ cell }) => (cell.getValue<boolean>() ? '✓' : '—') },
        ]}
        searchField="lastName"
        emptyMessage="Nessun dipendente"
        emptyDescription="Crea il primo dipendente con il bottone «Nuovo dipendente» nella toolbar."
        emptyIcon={IconUsers}
        editFields={FIELDS}
        editTitle="Modifica dipendente"
        enableDelete
        getRowLabel={(r) => `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || r.email || r.id}
        rowActions={(r) => (
          <Tooltip label="Mansioni">
            <ActionIcon
              variant="subtle"
              color="grape"
              aria-label="Mansioni"
              onClick={() => setRolesFor({ id: r.id, name: `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || r.email })}
            >
              <IconId size={16} />
            </ActionIcon>
          </Tooltip>
        )}
        toolbarRight={<QuickCreateButton type="erp-employees" title="Nuovo dipendente" fields={FIELDS} />}
      />
      <EmployeeRolesDrawer employee={rolesFor} onClose={() => setRolesFor(null)} />
    </>
  );
}
